import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';

import { LoginModal } from '@/components/ui/LoginModal';
import { useAuth } from '@/lib/auth';
import { fetchIndexRequests } from '../api';
import { PackageCatalogSection } from '../components/PackageCatalogSection';
import { navigateTo, useAppPathname } from '../router';
import type { IndexRequest } from '../types';
import { displayPackageName, formatIndexingProgress } from '../utils';

function SearchIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-4 w-4 text-dim"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
      />
    </svg>
  );
}

export function SearchPage() {
  useAppPathname();
  const params = useMemo(() => {
    if (typeof window === 'undefined') return new URLSearchParams();
    return new URLSearchParams(window.location.search);
  }, []);

  const { user, deploymentMode } = useAuth();
  const [showLogin, setShowLogin] = useState(false);
  // Private repos are a hosted-only feature, and the pitch is only useful to
  // someone who hasn't signed in yet.
  const showPrivateRepoPitch = deploymentMode === 'saas' && !user;

  const initialQuery = params.get('q') ?? '';
  const [query, setQuery] = useState(initialQuery);
  const [requests, setRequests] = useState<IndexRequest[]>([]);
  const [pollFailed, setPollFailed] = useState(false);
  const liveQuery = useDeferredValue(query.trim());

  useEffect(() => {
    const loadRequests = async () => {
      try {
        setRequests(await fetchIndexRequests());
        setPollFailed(false);
      } catch {
        // Retry on next poll.
        setPollFailed(true);
      }
    };

    void loadRequests();
    const interval = setInterval(() => void loadRequests(), 5_000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const nextUrl = liveQuery ? `/search?q=${encodeURIComponent(liveQuery)}` : '/search';
    window.history.replaceState({}, '', nextUrl);
  }, [liveQuery]);

  const searchInputRef = useRef<HTMLInputElement>(null);

  // Search is live (useDeferredValue), so Enter just commits: it dismisses
  // the on-screen keyboard on mobile and drops focus so results are visible.
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    searchInputRef.current?.blur();
  };

  const activeRequests = useMemo(
    () =>
      requests.filter(
        (request) =>
          request.status === 'pending_approval' ||
          request.status === 'pending' ||
          request.status === 'indexing'
      ),
    [requests]
  );

  const latestActiveRequest = activeRequests[0] ?? null;

  const latestActiveRequestMessage = useMemo(() => {
    if (!latestActiveRequest) return null;
    if (latestActiveRequest.error) return latestActiveRequest.error;
    if (latestActiveRequest.status === 'pending_approval')
      return latestActiveRequest.progress_message ?? 'Waiting for admin approval.';
    if (latestActiveRequest.status === 'pending')
      return latestActiveRequest.progress_message ?? 'Queued for indexing.';
    if (latestActiveRequest.status === 'indexing') {
      return (
        formatIndexingProgress(
          latestActiveRequest.progress_step,
          latestActiveRequest.progress_message
        ) ?? 'Indexing in progress.'
      );
    }
    return latestActiveRequest.progress_message ?? null;
  }, [latestActiveRequest]);

  return (
    // The shell's <main> already supplies the top padding (App.tsx) — only the
    // bottom gap is this page's to add.
    <div className="space-y-6 pb-6">
      {/* Slim hero band. This is an app surface reached from the nav, so it uses
          the app-page heading scale (globals.css) rather than the marketing
          SectionHeading — the catalog itself should be what's above the fold. */}
      <section className="flex flex-col gap-4 border-b border-rule pb-5 lg:flex-row lg:items-start lg:justify-between lg:gap-8">
        <div className="min-w-0 lg:max-w-xl">
          {/* The active nav tab already names the page, so the heading is
              visually redundant — kept for screen readers and document outline. */}
          <h1 className="sr-only">All libraries</h1>
          <p className="max-w-3xl text-sm leading-relaxed text-dim">
            Thanks for stopping by! Below, you'll find the languages and libraries we currently
            support. Each package is indexed at a specific version—open one to ask questions
            answered directly from that version's source.
          </p>
          <div className="mt-2 space-y-1 text-sm leading-relaxed text-dim">
            <p>
              Need another language?{' '}
              <button
                type="button"
                onClick={() => navigateTo('/contact')}
                className="font-medium text-accent transition-colors hover:underline"
              >
                Let us know →
              </button>
            </p>
            <p>
              Missing a package or version?{' '}
              <button
                type="button"
                onClick={() => navigateTo('/request')}
                className="font-medium text-accent transition-colors hover:underline"
              >
                Request it →
              </button>
            </p>
            {showPrivateRepoPitch ? (
              <p>
                Want to use a private repo?{' '}
                <button
                  type="button"
                  onClick={() => setShowLogin(true)}
                  className="font-medium text-accent transition-colors hover:underline"
                >
                  Create a free account
                </button>{' '}
                with just your email—no credit card required.
              </p>
            ) : null}
          </div>
        </div>

        <div className="w-full lg:max-w-md">
          <form onSubmit={handleSubmit}>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <SearchIcon />
              </div>
              <input
                ref={searchInputRef}
                data-testid="search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search libraries, ecosystems, or names"
                className="h-11 w-full border border-rule bg-panel pl-10 pr-4 text-sm text-ink outline-none transition-colors placeholder:text-dim focus:border-accent focus:ring-1 focus:ring-accent"
              />
            </div>
          </form>

          <div className="mt-2 space-y-1.5 text-xs lg:text-right">
            {latestActiveRequest ? (
              <p className="inline-flex max-w-full items-center gap-1.5 text-dim">
                <span className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-accent" />
                <span className="truncate">
                  {activeRequests.length} active request{activeRequests.length === 1 ? '' : 's'} ·{' '}
                  <span className="font-medium text-link">
                    {displayPackageName(latestActiveRequest.registry, latestActiveRequest.name)}
                  </span>
                  {latestActiveRequestMessage ? `: ${latestActiveRequestMessage}` : ''}
                  {pollFailed ? (
                    <span className="italic opacity-70"> (status may be stale)</span>
                  ) : null}
                </span>
              </p>
            ) : null}
          </div>
        </div>
      </section>

      <PackageCatalogSection searchQuery={liveQuery} />

      {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}
    </div>
  );
}
